<template lang="pug">
VTextField(
  v-model="title"
  :label="i18n.t('ReadingList.AddNewList')"
  :placeholder="i18n.t('ReadingList.ListName')"
  :clearable="true"
  :density="density"
  :variant="variant"
  :error-messages="titleError"
  @keydown.enter="createList"
)
  template(#append)
    VBtn.mr-2(
      :submit="true"
      :disabled="!allowCreate"
      color="primary"
      :icon="true"
      :size="submitIconSize"
      variant="elevated"
      @click="createList"
    )
      VIcon {{ mdiPlus }}
</template>

<script setup lang="ts">
import { mdiPlus } from "@mdi/js";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import {
  getListNameFromTitle,
  useReadingListsStore,
} from "~/stores/useReadingListsStore";

const i18n = useI18n();
const readingListsStore = useReadingListsStore();

interface Props {
  /**
   * The IDs of comments to add to the new list when it is created.
   */
  commentIds?: number[];

  density?: "default" | "comfortable" | "compact";

  submitIconSize?: "small" | "x-small";

  variant?:
    | "outlined"
    | "plain"
    | "underlined"
    | "filled"
    | "solo"
    | "solo-inverted"
    | "solo-filled";
}

const props = withDefaults(defineProps<Props>(), {
  commentIds: () => [],
  submitIconSize: "x-small",
});

const title = ref<string | null>(""); // Clearing the input field sets this to null instead of "".

const titleEmpty = computed<boolean>(() => (title.value || "").length < 1);

const titleError = computed<string | undefined>(() => {
  if (titleEmpty.value) {
    return undefined;
  }

  const name = getListNameFromTitle(title.value || "");
  if (readingListsStore.readingListsNames.includes(name)) {
    return i18n.t("ReadingList.errorSameName");
  }

  return undefined;
});

const titleValid = computed<boolean>(() => {
  return titleError.value === undefined;
});

const allowCreate = computed<boolean>(() => {
  return titleValid.value && !titleEmpty.value;
});

function createList() {
  if (!allowCreate.value) {
    return;
  }

  const newTitle = title.value || "";
  title.value = "";

  const addedDate = Date.now();
  const items = props.commentIds.map((commentId) => ({
    commentId,
    addedDate,
  }));
  readingListsStore.addToList(readingListsStore.newList(newTitle), items);
}
</script>
